import { Head, Link } from '@inertiajs/react';
import { Button } from '../components/ui/button';
import {
  Target,
  Users,
  Award,
  CheckCircle,
  Clock,
  Gift,
  ArrowRight,
  MessageSquare,
  Handshake
} from 'lucide-react';

export default function PhysiotherapistInfo() {
  return (
    <>
      <Head title="Rejoignez Ethos Santé - Pour les kinésithérapeutes" />
      
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#008788]/10 to-[#31953a]/10 border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Rejoignez Ethos Santé
              </h1>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                <strong>La plateforme qui valorise vos compétences et aide les patients à trouver le bon kiné, selon leurs besoins.</strong>
              </p>
              <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
                Ethos Santé est un outil conçu <strong>par et pour les kinésithérapeutes</strong>, pour mieux orienter les patients et mettre en avant vos <strong>spécialités, formations et expertises cliniques</strong>.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Link href="/registration/new">
                  <Button size="lg" className="bg-[#008788] hover:bg-[#015963] text-white text-base px-8 w-full sm:w-auto">
                    Je crée mon profil
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link href="/session/new">
                  <Button size="lg" variant="outline" className="text-base px-8 w-full sm:w-auto">
                    Se connecter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Pourquoi s'inscrire */}
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12 mb-12">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                💡 Pourquoi s'inscrire ?
              </h2>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#31953a] mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Valorisez votre pratique
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Mettez en avant vos <strong>compétences réelles</strong>, vos spécialisations (sport, pédiatrie, vestibulaire, respi, etc.) et votre approche du soin.
                  </p>
                  <p className="text-gray-700 mt-2 leading-relaxed">
                    Chaque profil est référencé selon des <strong>critères objectifs et vérifiés</strong>.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#31953a] mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Soyez trouvé·e par les bons patients
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Les patients recherchent un <strong>kiné adapté à leur pathologie</strong> (entorse, post-op, rééducation spécifique…).
                  </p>
                  <p className="text-gray-700 mt-2 leading-relaxed">
                    Ethos Santé facilite la <strong>mise en relation intelligente</strong> entre besoin et expertise.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#31953a] mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Participez à une démarche de qualité
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Vous contribuez à une <strong>meilleure orientation dans le parcours de soins</strong> et à une meilleure reconnaissance du métier.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Gratuit pour les 200 premiers */}
          <div className="bg-gradient-to-r from-[#008788]/10 to-[#31953a]/10 rounded-xl p-8 md:p-12 mb-12 border-2 border-[#008788]/20">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-[#008788]/20 rounded-full mr-4">
                <Gift className="h-8 w-8 text-[#008788]" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Gratuit pour les 200 premiers inscrits
              </h2>
            </div>
            
            <p className="text-lg text-gray-700 mb-6">
              L'abonnement annuel Ethos Santé comprend :
            </p>
            
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-[#008788] mr-3 mt-1">•</span>
                <span className="text-gray-700">votre <strong>référencement complet</strong> sur la plateforme,</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#008788] mr-3 mt-1">•</span>
                <span className="text-gray-700">un accès à vos <strong>statistiques de visibilité</strong>,</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#008788] mr-3 mt-1">•</span>
                <span className="text-gray-700">la possibilité de <strong>répondre à des questionnaires de spécialité</strong> pour affiner votre profil.</span>
              </li>
            </ul>
            
            <div className="bg-white rounded-lg p-4 border border-[#008788]/30">
              <p className="text-lg font-semibold text-gray-900">
                👉 Les 200 premiers kinés inscrits bénéficient d'un accès <strong className="text-[#008788]">gratuit à vie</strong> sur la plateforme.
              </p>
            </div>
          </div>

          {/* Comment ça marche */}
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12 mb-12">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-[#008788]/10 rounded-full mr-4">
                <Clock className="h-8 w-8 text-[#008788]" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Comment ça marche ?
              </h2>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#008788] text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Créez votre profil (10 min chrono)
                  </h3>
                  <p className="text-gray-600">
                    Nom, cabinet, spécialités, infos prise de rdv.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#008788] text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Complétez vos domaines d'expertise
                  </h3>
                  <p className="text-gray-600">
                    via un ou plusieurs questionnaires thématiques.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#008788] text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Validez votre inscription et recevez votre lien public Ethos Santé
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* Notre mission */}
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12 mb-12">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-gray-200 rounded-full mr-4">
                <Handshake className="h-8 w-8 text-[#31953a]" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Notre mission
              </h2>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#008788]">
              <blockquote className="text-gray-700 text-lg leading-relaxed space-y-4">
                <p>
                  Chez Ethos Santé, nous croyons que chaque patient mérite d'être orienté vers le kinésithérapeute le plus adapté à son besoin.
                </p>
                <p>
                  Nous croyons aussi que chaque kiné mérite d'être reconnu pour sa <strong>vraie expertise</strong>, au-delà du simple code postal.
                </p>
              </blockquote>
            </div>
          </div>

          {/* Rejoignez la communauté */}
          <div className="bg-gradient-to-br from-[#008788] to-[#00bcbe] rounded-xl shadow-lg p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-8">
              📩 Rejoignez la communauté
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <Clock className="h-8 w-8 mx-auto mb-3" />
                <p className="font-semibold">Moins de 10 minutes pour créer votre profil</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <Target className="h-8 w-8 mx-auto mb-3" />
                <p className="font-semibold">Gratuit pour les 200 premiers</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <MessageSquare className="h-8 w-8 mx-auto mb-3" />
                <p className="font-semibold">Une équipe dispo pour vous accompagner</p>
              </div>
            </div>
            
            <Link href="/registration/new" className="inline-block w-full sm:w-auto">
              <Button
                size="lg"
                className="bg-white text-[#008788] hover:bg-gray-100 text-lg px-8 py-6 font-semibold w-full sm:w-auto whitespace-normal"
              >
                Je crée mon profil kiné maintenant
                <ArrowRight className="h-5 w-5 ml-2 flex-shrink-0" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

